import React from "react";
import { Check, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Pricing() {
    const { t } = useTranslation();

    const tiers = [
        {
            name: "Starter",
            credits: 5,
            price: "€1.99",
            per: "€0.40",
            tagline: t("landing.pricing.taglineStarter"),
            cta: t("landing.pricing.ctaStarter"),
            highlight: false,
        },
        {
            name: "Standard",
            credits: 15,
            price: "€4.99",
            per: "€0.33",
            tagline: t("landing.pricing.taglineStandard"),
            cta: t("landing.pricing.ctaStandard"),
            highlight: true,
        },
        {
            name: "Hunter",
            credits: 40,
            price: "€9.99",
            per: "€0.25",
            tagline: t("landing.pricing.taglineHunter"),
            cta: t("landing.pricing.ctaHunter"),
            highlight: false,
        },
    ];

    const buildFeatures = (credits) => [
        { ok: true, label: t("landing.pricing.fullReports", { count: credits }) },
        { ok: true, label: t("landing.pricing.chromeExtension") },
        { ok: true, label: t("landing.pricing.compareCollections") },
        { ok: true, label: t("landing.pricing.bulkAnalysis") },
    ];

    return (
        <section
            id="pricing"
            className="scroll-mt-20 border-b-2 border-black bg-white"
            data-testid="pricing-section"
        >
            <div className="border-b-2 border-black">
                <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                    <div className="col-span-12 md:col-span-5 border-r-0 md:border-r-2 border-b-2 md:border-b-0 border-black p-8 md:p-12">
                        <span className="font-mono text-xs tracking-[0.25em] uppercase border-2 border-black bg-black text-white px-3 py-1 inline-block">
                            {t("landing.pricing.eyebrow")}
                        </span>
                        <h2 className="mt-6 font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter">
                            {t("landing.pricing.headlineLine1")}
                            <br />
                            {t("landing.pricing.headlineLine2")}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 p-8 md:p-12 flex items-end">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-700">
                            {t("landing.pricing.description")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tiers */}
            <div className="bg-zinc-50 py-12 px-6 md:px-12">
                <div className="mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-3 gap-6">
                    {tiers.map((tier) => (
                        <article
                            key={tier.name}
                            data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                            className={`p-8 md:p-10 flex flex-col border-2 border-black shadow-brutal ${
                                tier.highlight
                                    ? "bg-black text-white"
                                    : "bg-white text-black"
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <span
                                    className={`font-mono text-[10px] tracking-[0.25em] uppercase border-2 px-3 py-1 ${
                                        tier.highlight
                                            ? "border-white bg-[#FFCC00] text-black"
                                            : "border-black bg-white"
                                    }`}
                                >
                                    {tier.tagline}
                                </span>
                                <span
                                    className={`font-mono text-[10px] tracking-[0.25em] uppercase ${tier.highlight ? "text-zinc-400" : "text-zinc-500"}`}
                                >
                                    {t("landing.pricing.credits", { count: tier.credits })}
                                </span>
                            </div>

                            <h3 className="mt-6 font-display text-3xl md:text-4xl uppercase tracking-tight">
                                {tier.name}
                            </h3>

                            <div className="mt-6 flex items-baseline gap-3">
                                <span className="font-display text-5xl md:text-6xl leading-none">
                                    {tier.price}
                                </span>
                                <span
                                    className={`font-mono text-xs uppercase tracking-[0.2em] ${tier.highlight ? "text-zinc-400" : "text-zinc-500"}`}
                                >
                                    {t("landing.pricing.oneTime")}
                                </span>
                            </div>
                            <div
                                className={`mt-2 font-mono text-[11px] uppercase tracking-[0.2em] ${tier.highlight ? "text-zinc-400" : "text-zinc-500"}`}
                            >
                                {t("landing.pricing.perAnalysis", { price: tier.per })}
                            </div>

                            <div
                                className={`mt-8 border-t-2 ${tier.highlight ? "border-white/30" : "border-black/20"}`}
                            />

                            <ul className="mt-6 space-y-3 flex-1">
                                {buildFeatures(tier.credits).map((f) => (
                                    <li
                                        key={f.label}
                                        className="flex items-start gap-3 text-sm"
                                    >
                                        {f.ok ? (
                                            <Check
                                                size={16}
                                                className="mt-0.5 flex-shrink-0"
                                            />
                                        ) : (
                                            <Minus
                                                size={16}
                                                className={`mt-0.5 flex-shrink-0 ${tier.highlight ? "text-zinc-500" : "text-zinc-400"}`}
                                            />
                                        )}
                                        <span>{f.label}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className={`mt-3 font-mono text-[10px] uppercase tracking-[0.15em] ${tier.highlight ? "text-zinc-400" : "text-zinc-500"}`}>
                                {t("landing.pricing.allFeaturesNote")}
                            </div>

                            <a
                                href="#cta"
                                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                                className={`btn-brutal mt-8 block text-center border-2 px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] shadow-brutal-sm ${
                                    tier.highlight
                                        ? "bg-[#FFCC00] border-white text-black"
                                        : "bg-black border-black text-white"
                                }`}
                            >
                                {tier.cta} →
                            </a>
                        </article>
                    ))}
                </div>
            </div>

            {/* Comparison strip */}
            <div className="border-t-2 border-black">
                <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                    <div className="col-span-12 md:col-span-4 border-r-0 md:border-r-2 border-b-2 md:border-b-0 border-black p-6 flex items-center gap-4">
                        <span className="font-display text-3xl">≈ €5</span>
                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600 leading-snug">
                            {t("landing.pricing.compCost15")}
                        </span>
                    </div>
                    <div className="col-span-12 md:col-span-4 border-r-0 md:border-r-2 border-b-2 md:border-b-0 border-black p-6 flex items-center gap-4">
                        <span className="font-display text-3xl">€80–120</span>
                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600 leading-snug">
                            {t("landing.pricing.compInspection")}
                        </span>
                    </div>
                    <div className="col-span-12 md:col-span-4 p-6 flex items-center gap-4 bg-[#FFCC00] !text-[#000]">
                        <span className="font-display text-3xl !text-[#000]">€3,400</span>
                        <span className="font-mono text-xs uppercase tracking-[0.2em] !text-[#000] leading-snug">
                            {t("landing.pricing.compLemon")}
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}
